import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { DataTable } from '../components/DataTable';
import { Modal } from '../components/Modal';
import { Button } from '../components/Button';
import { useToast } from '../components/Toast';
import { formatoStock } from '../components/StockSku';
import { api, extractErrorMessage } from '../api/client';
import { useXlsxExport } from '../hooks/useXlsxExport';
import { BuscadorSku, type SkuSugerido } from '../components/BuscadorSku';
import { asegurarArticulos, cargarIdentidades, etiquetaArticulo, recordarIdentidad, textoArticulo, useFilasConIdentidad, useIdentidades } from '../lib/identidadArticulos';
import { CodigoArticulo } from '../components/CodigoArticulo';
import { VerInstructivo } from '../components/VerInstructivo';
import { PASOS_VARIANTES, type FilaMaestroVariante, type ResultadoImportVariantes, type VinculosDeVariante } from '../lib/variantes';

const COLOR_NEUTRO = '#cccccc';

// Función de módulo: el buscador la necesita estable.
const buscarSkus = (texto: string) => api.get<SkuSugerido[]>(`/api/v3/catalogo/buscar?q=${encodeURIComponent(texto)}`);
const resolverSkus = (codigo: string) => api.get<SkuSugerido[]>(`/api/v3/catalogo/buscar?codigos=${encodeURIComponent(codigo)}`);

/** Maestro de variantes: todas las variantes de los SKU físicos. Alta, renombrar, color y desactivar (nunca se borran). */
export function VariantesPage() {
  const { data, loading, refetch } = useApi<FilaMaestroVariante[]>('/api/v3/catalogo/variantes');
  const [params] = useSearchParams();
  const [busqueda, setBusqueda] = useState(params.get('sku') ?? '');
  const [inactivas, setInactivas] = useState(false);
  const [alta, setAlta] = useState<{ sku: string; nombre: string; conColor: boolean; color: string } | null>(null);
  const [renombrar, setRenombrar] = useState<{ fila: FilaMaestroVariante; nombre: string } | null>(null);
  /** Confirmación previa cuando la variante tiene publicaciones o combos ligados. */
  const [aviso, setAviso] = useState<{ accion: 'renombrar' | 'desactivar'; fila: FilaMaestroVariante; nombre: string; vinculos: VinculosDeVariante } | null>(null);
  const [importacion, setImportacion] = useState<{ archivo: File; vista: ResultadoImportVariantes } | null>(null);
  const [importando, setImportando] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const enCurso = useRef(false); // la ref frena un segundo clic antes de que el estado deshabilite el botón
  const toast = useToast();
  const { exportXlsx } = useXlsxExport();
  const puedeEditar = useAuth().can('catalogo', 'editar');
  const sinPermiso = puedeEditar ? undefined : 'No tiene permiso para realizar esta acción';
  // Only reads the cache (picked articles): the field looks up its own committed SKU.
  const identidad = useIdentidades([]);

  const q = busqueda.trim().toLowerCase();
  const filas = useFilasConIdentidad(data ?? [], (f) => f.skuInterno).filter((f) => (inactivas || f.activo)
    && (!q || f.skuInterno.toLowerCase().includes(q) || f.codigoArticulo.toLowerCase().includes(q) || f.nombre.toLowerCase().includes(q)));

  /** Ejecuta una escritura; si sale bien refresca la lista y devuelve true. */
  const escribir = async (llamada: () => Promise<unknown>) => {
    try { await llamada(); refetch(); return true; }
    catch (e) { toast.addToast(extractErrorMessage(e), 'error'); return false; }
  };
  const patchFila = (f: FilaMaestroVariante, cambios: Record<string, unknown>) =>
    escribir(() => api.patch(`/api/v3/catalogo/${encodeURIComponent(f.skuInterno)}/variantes/${f.id}`, cambios));

  const guardarAlta = async (agregarOtra = false) => {
    if (!alta) return;
    // A typed code not resolved yet: one article is used, several block.
    const { sku: resueltos, ambiguos } = await asegurarArticulos([alta.sku], resolverSkus);
    if (ambiguos.length > 0) { toast.addToast(ambiguos[0], 'error'); return; }
    const skuInterno = resueltos.get(alta.sku.trim()) ?? alta.sku.trim();
    const ok = await escribir(() => api.post('/api/v3/catalogo/variantes', {
      skuInterno, nombre: alta.nombre.trim(), colorHex: alta.conColor ? alta.color : null,
    }));
    if (!ok) return;
    toast.addToast(`Variante «${alta.nombre.trim()}» agregada a ${textoArticulo(skuInterno, identidad(skuInterno))}`, 'success');
    if (!agregarOtra) { setAlta(null); return; }
    setAlta({ ...alta, sku: skuInterno, nombre: '', conColor: false, color: COLOR_NEUTRO });
    setTimeout(() => document.getElementById('var-nombre')?.focus(), 0);
  };
  /** Publicaciones y combos ligados a la variante; null (con aviso) si no se pudo consultar: sin saberlo no se cambia nada. */
  const consultarVinculos = async (f: FilaMaestroVariante) => {
    try { return await api.get<VinculosDeVariante>(`/api/v3/catalogo/${encodeURIComponent(f.skuInterno)}/variantes/${f.id}/vinculos`); }
    catch (e) { toast.addToast(extractErrorMessage(e), 'error'); return null; }
  };
  const conVinculos = (v: VinculosDeVariante) => v.publicaciones.length + v.combos.length > 0;
  /** Una sola operación a la vez (renombrar, desactivar, confirmar): un doble clic no manda dos pedidos. */
  const unaVez = async (fn: () => Promise<void>) => {
    if (enCurso.current) return;
    enCurso.current = true; setOcupado(true);
    try { await fn(); } finally { enCurso.current = false; setOcupado(false); }
  };
  const guardarNombre = () => unaVez(async () => {
    if (!renombrar) return;
    const nombre = renombrar.nombre.trim();
    const vinculos = await consultarVinculos(renombrar.fila);
    if (!vinculos) return;
    if (conVinculos(vinculos)) { setAviso({ accion: 'renombrar', fila: renombrar.fila, nombre, vinculos }); setRenombrar(null); return; }
    if (await patchFila(renombrar.fila, { nombre })) setRenombrar(null);
  });
  const pedirDesactivar = (f: FilaMaestroVariante) => unaVez(async () => {
    const vinculos = await consultarVinculos(f);
    if (!vinculos) return;
    if (conVinculos(vinculos)) setAviso({ accion: 'desactivar', fila: f, nombre: f.nombre, vinculos });
    else await patchFila(f, { activo: false });
  });
  const confirmarAviso = () => unaVez(async () => {
    if (!aviso) return;
    const ok = await patchFila(aviso.fila, aviso.accion === 'renombrar' ? { nombre: aviso.nombre } : { activo: false });
    if (ok) setAviso(null);
  });
  const cerrarImportacion = () => { if (!importando) setImportacion(null); };

  const URL_IMPORTAR = '/api/v3/catalogo/variantes/importar';
  const elegirArchivo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const archivo = e.target.files?.[0];
    e.target.value = ''; // para que elegir el mismo archivo otra vez dispare onChange
    if (!archivo) return;
    try { setImportacion({ archivo, vista: await api.upload<ResultadoImportVariantes>(`${URL_IMPORTAR}?dryRun=true`, archivo) }); }
    catch (err) { toast.addToast(extractErrorMessage(err), 'error'); }
  };
  const aplicarImportacion = async () => {
    if (!importacion) return;
    setImportando(true);
    try {
      const r = await api.upload<ResultadoImportVariantes>(`${URL_IMPORTAR}?dryRun=false`, importacion.archivo);
      toast.addToast(`Variantes importadas: ${r.nuevas.length} nuevas, ${r.cambios.length} actualizadas`, 'success');
      setImportacion(null);
      refetch();
    } catch (err) { toast.addToast(extractErrorMessage(err), 'error'); }
    finally { setImportando(false); }
  };
  // Solo las activas: el archivo exportado se reimporta sin cambios (la importación reactiva las que aparecen).
  // Cod is the article's code and Proveedor its identity supplier, which the import resolves back to the same article.
  const exportar = async () => {
    const activas = filas.filter((f) => f.activo);
    const identidades = await cargarIdentidades(activas.map((f) => f.skuInterno));
    return exportXlsx(
      [{ key: 'cod', header: 'Cod' }, { key: 'proveedor', header: 'Proveedor' }, { key: 'variante', header: 'Variante' }, { key: 'color', header: 'Color' }, { key: 'activa', header: 'Activa' }, { key: 'stock', header: 'Stock' }],
      activas.map((f) => {
        const i = identidades.get(f.skuInterno);
        return { cod: textoArticulo(f.skuInterno, i), proveedor: i?.proveedor?.nombre ?? '', variante: f.nombre, color: f.colorHex ?? '', activa: f.activo ? 'Sí' : 'No', stock: f.cantidad };
      }),
      'Variantes',
    );
  };

  const columns = [
    { key: 'codigoArticulo', header: 'Código', render: (f: FilaMaestroVariante) => <CodigoArticulo sku={f.skuInterno} /> },
    { key: 'proveedorArticulo', header: 'Proveedor' },
    { key: 'nombre', header: 'Variante' },
    {
      key: 'colorHex', header: 'Color', filterable: false,
      render: (f: FilaMaestroVariante) => (
        <input type="color" key={f.colorHex ?? ''} aria-label={`Color de ${f.nombre}`} defaultValue={f.colorHex ?? COLOR_NEUTRO} disabled={!puedeEditar}
          onBlur={(e) => {
            const input = e.target;
            const anterior = f.colorHex ?? COLOR_NEUTRO;
            // Si el PATCH falla, el selector vuelve al color guardado (el refetch no corre y la key no cambia).
            if (input.value !== anterior) void patchFila(f, { colorHex: input.value }).then((ok) => { if (!ok) input.value = anterior; });
          }} />
      ),
    },
    { key: 'cantidad', header: 'Stock', align: 'right' as const, render: (f: FilaMaestroVariante) => formatoStock(f.cantidad) },
    { key: 'activo', header: 'Activa', render: (f: FilaMaestroVariante) => (f.activo ? 'Sí' : 'No') },
    {
      key: 'acciones', header: '', filterable: false,
      render: (f: FilaMaestroVariante) => (
        <span className="flex gap-3 text-xs font-medium">
          <button disabled={!puedeEditar} title={sinPermiso} className="text-brand-600 hover:underline disabled:opacity-50"
            onClick={() => setRenombrar({ fila: f, nombre: f.nombre })}>Renombrar</button>
          <button disabled={!puedeEditar} title={sinPermiso} className="text-slate-600 hover:underline disabled:opacity-50"
            onClick={() => void (f.activo ? pedirDesactivar(f) : patchFila(f, { activo: true }))}>{f.activo ? 'Desactivar' : 'Reactivar'}</button>
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-y-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 inline">Variantes</h1><VerInstructivo seccion="variantes-por-color" />
          <p className="text-slate-500 text-sm mt-1">Variantes de cada SKU físico, con su color y su stock</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label title={sinPermiso} className={`px-4 py-2 text-sm border rounded-lg ${puedeEditar ? 'cursor-pointer hover:bg-slate-50' : 'opacity-50 cursor-not-allowed'}`}>
            Importar desde Excel
            <input type="file" accept=".xlsx" className="hidden" disabled={!puedeEditar} onChange={elegirArchivo} />
          </label>
          <button onClick={() => void exportar()} className="px-4 py-2 text-sm border rounded-lg hover:bg-slate-50">Exportar a Excel</button>
          <Button disabled={!puedeEditar} onClick={() => setAlta({ sku: '', nombre: '', conColor: false, color: COLOR_NEUTRO })}>
            Agregar variante
          </Button>
        </div>
      </div>

      <details open className="bg-white rounded-xl border border-slate-200 p-4 text-sm text-slate-600">
        <summary className="cursor-pointer font-semibold text-slate-800">Cómo se trabaja con variantes</summary>
        <div className="mt-3 space-y-3">
          {PASOS_VARIANTES.map((e, i) => (
            <div key={e.etapa}>
              <p className="font-medium text-slate-700">{e.etapa}</p>
              <ol start={PASOS_VARIANTES.slice(0, i).reduce((n, x) => n + x.pasos.length, 1)} className="list-decimal pl-5 space-y-1">
                {e.pasos.map(p => <li key={p}>{p}</li>)}
              </ol>
            </div>
          ))}
        </div>
      </details>

      <div className="flex flex-wrap items-center gap-4">
        <input aria-label="Buscar por código o variante" placeholder="Buscar por código o variante" value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)} className="px-3 py-2 border rounded-lg w-full sm:w-80" />
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={inactivas} onChange={(e) => setInactivas(e.target.checked)} />
          Mostrar inactivas
        </label>
      </div>

      <DataTable columns={columns} data={filas} loading={loading} emptyMessage="No hay variantes" enableXlsxExport={false} title="Variantes" />

      <Modal open={!!alta} onClose={() => setAlta(null)} title="Agregar variante" size="sm">
        {alta && (
          <div className="space-y-4">
            <div>
              <label htmlFor="var-sku" className="block text-sm font-medium mb-1">Código</label>
              <BuscadorSku id="var-sku" value={alta.sku} mostrarCodigo mostrarProveedor placeholder="SKU o descripción" autoFocus
                buscar={buscarSkus} resolver={resolverSkus}
                onChange={(sku) => setAlta({ ...alta, sku })} onElegir={(s) => { recordarIdentidad(s); setAlta({ ...alta, sku: s.skuInterno }); }} />
            </div>
            <div>
              <label htmlFor="var-nombre" className="block text-sm font-medium mb-1">Variante</label>
              <input id="var-nombre" value={alta.nombre} onChange={(e) => setAlta({ ...alta, nombre: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={alta.conColor} onChange={(e) => setAlta({ ...alta, conColor: e.target.checked })} />
                Con color
              </label>
              {alta.conColor && <input type="color" aria-label="Color de la variante" value={alta.color} onChange={(e) => setAlta({ ...alta, color: e.target.value })} />}
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button onClick={() => setAlta(null)} className="px-4 py-2 text-sm">Cancelar</button>
              <Button onClick={() => guardarAlta()} disabled={!alta.sku.trim() || !alta.nombre.trim()}>Guardar</Button>
              <Button onClick={() => guardarAlta(true)} disabled={!alta.sku.trim() || !alta.nombre.trim()}>Guardar y agregar otra</Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!importacion} onClose={cerrarImportacion} title="Importar variantes" size="lg">
        {importacion && (() => {
          const { nuevas, cambios, errores, sinCambios } = importacion.vista;
          const reactivan = cambios.filter((c) => c.reactivar);
          const cambianColor = cambios.filter((c) => c.colorNuevo);
          return (
            <div className="space-y-4 text-sm">
              <section>
                <h3 className="font-semibold">Variantes nuevas ({nuevas.length})</h3>
                <ul className="list-disc pl-5">{nuevas.map((n) => <li key={`${n.skuInterno}|${n.nombreNormalizado}`}>{n.skuInterno} · {n.nombre}</li>)}</ul>
              </section>
              <section>
                <h3 className="font-semibold">Colores que cambian ({cambianColor.length})</h3>
                <ul className="list-disc pl-5">{cambianColor.map((c) => <li key={c.id}>{c.skuInterno} · {c.nombre}: {c.colorAnterior ?? 'sin color'} → {c.colorNuevo}</li>)}</ul>
              </section>
              <section>
                <h3 className="font-semibold">Se reactivan ({reactivan.length})</h3>
                <ul className="list-disc pl-5">{reactivan.map((c) => <li key={c.id}>{c.skuInterno} · {c.nombre}</li>)}</ul>
              </section>
              <section>
                <h3 className="font-semibold">Filas con error ({errores.length})</h3>
                <ul className="list-disc pl-5 text-red-700">{errores.map((e, i) => <li key={i}>{`Fila ${e.fila}: ${e.motivo}`}</li>)}</ul>
              </section>
              <p>Sin cambios: {sinCambios}</p>
              <p className="text-slate-500">No se borra ni se desactiva ninguna variante que no esté en el archivo.</p>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button onClick={cerrarImportacion} disabled={importando} className="px-4 py-2 text-sm disabled:opacity-50">Cancelar</button>
                <Button onClick={aplicarImportacion} disabled={importando || (nuevas.length === 0 && cambios.length === 0)}>Aplicar</Button>
              </div>
            </div>
          );
        })()}
      </Modal>

      <Modal open={!!renombrar} onClose={() => setRenombrar(null)} title={renombrar ? `Renombrar variante de ${etiquetaArticulo(renombrar.fila.skuInterno, identidad(renombrar.fila.skuInterno))}` : ''} size="sm">
        {renombrar && (
          <div className="space-y-4">
            <div>
              <label htmlFor="var-renombrar" className="block text-sm font-medium mb-1">Variante</label>
              <input id="var-renombrar" value={renombrar.nombre} onChange={(e) => setRenombrar({ ...renombrar, nombre: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg" autoFocus />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button onClick={() => setRenombrar(null)} className="px-4 py-2 text-sm">Cancelar</button>
              <Button onClick={guardarNombre} disabled={ocupado || !renombrar.nombre.trim()}>Guardar</Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!aviso} onClose={() => setAviso(null)} size="sm"
        title={aviso ? `${aviso.accion === 'renombrar' ? 'Renombrar' : 'Desactivar'} ${aviso.fila.skuInterno} · ${aviso.fila.nombre}` : ''}>
        {aviso && (
          <div className="space-y-4 text-sm">
            <p>Esta variante está ligada a:</p>
            <ul className="list-disc pl-5 max-h-48 overflow-y-auto">
              {aviso.vinculos.publicaciones.map((p) => (
                <li key={`${p.mlaId}/${p.variantId}/${p.skuVenta}`}>Publicación {p.variantId ? `${p.mlaId}/${p.variantId}` : p.mlaId} · SKU {p.skuVenta}{p.titulo ? ` · ${p.titulo}` : ''}</li>
              ))}
              {aviso.vinculos.combos.map((c) => <li key={c.skuInterno}>Combo o pack {c.skuInterno}{c.descripcion ? ` · ${c.descripcion}` : ''}</li>)}
            </ul>
            {aviso.accion === 'renombrar' ? (
              <p>Pasa a llamarse «{aviso.nombre}». Las publicaciones siguen ligadas; cambiá también el nombre en la columna G / varN de 02_CODIGOS. Hasta entonces la sincronización lo avisa como planilla desactualizada.</p>
            ) : (
              <p>En la próxima sincronización de 02_CODIGOS estas publicaciones quedan sin variante (con el switch de ML prendido publican 0).</p>
            )}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button onClick={() => setAviso(null)} className="px-4 py-2 text-sm">Cancelar</button>
              <Button onClick={confirmarAviso} disabled={ocupado}>{aviso.accion === 'renombrar' ? 'Renombrar' : 'Desactivar'}</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
